import React from "react";
import PropTypes from "prop-types";
import {makeStyles} from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import clsx from "clsx";
import {CircularProgress} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
    buttonWrapper: {
        position: "relative",
    },
    button: {
        margin: theme.spacing(1)
    },
    small: {
        fontSize: theme.spacing(1.75),
        padding: theme.spacing(1.5, 2),
        height: theme.spacing(4.5)
    },
    medium: {
        fontSize: theme.spacing(1.75),
        padding: theme.spacing(1.75, 3),
        height: theme.spacing(5)
    },
    large: {
        fontSize: theme.spacing(2),
        padding: theme.spacing(2.25, 4),
        height: theme.spacing(6)
    },
    white: {
        background: theme.palette.grey["0"] + " !important",
        color: theme.palette.text.black + " !important",
        border: "none !important",
    },
    whiteText: {
        color: theme.palette.text.white + " !important",
        border: "none !important",
    },
    disabled: {
        background: theme.palette.grey["100"] + " !important",
        borderRadius: "6px",
        color: theme.palette.text.disabledStrong + " !important",
        border: "none !important"
    },
    textDisabled: {
        background: "unset !important"
    },
    hidden: {
        visibility: "hidden"
    },
    blackText: {
        // TODO: according to the designs the color is not total black, but the primary one and there is no border
        // color: theme.palette.black + " !important",
        "&:hover": {
            // border: "1px solid" + theme.palette.secondary["200"],
            backgroundColor: "rgba(0, 0, 0, 0.08)",
            borderRadius: theme.spacing(0.75)
        }
    },
    negative: {
        border: "1px solid " + theme.palette.grey[0],
        color: theme.palette.grey[0] + " !important",
        "&:hover": {
            border: "1px solid" + theme.palette.secondary["200"],
            backgroundColor: "rgba(255, 255, 255, 0.08)",
        }
    },
    danger: {
        "&.outlined": {
            color: theme.palette.error.danger,
            "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.08)",
                borderRadius: theme.spacing(0.75)
            }
        },
        "&.contained": {
            backgroundColor: theme.palette.error.danger,
            "&:hover": {
                backgroundColor: "#cf4445"
            }
        },
    },
}));

const CustomButton = ({
    children,
    color,
    disabled,
    disableElevation,
    disableFocusRipple,
    disableRipple,
    endIcon,
    fullWidth,
    href,
    onClick,
    size,
    startIcon,
    variant,
    white,
    onBlur,
    onFocus,
    loading,
    className,
    buttonRef,
    ...props
}) => {

    const classes = useStyles();

    const handleClick = () => {
        if (onClick && !loading) {
            onClick();
        }
    };

    const handleFocus = () => {
        if (onFocus) {
            onFocus();
        }
    };

    const handleBlur = () => {
        if (onBlur) {
            onBlur();
        }
    };


    return (
        <Button
            {...props}
            className={clsx(className, variant, classes.buttonWrapper,
                {[classes.small]: size === "small"},
                {[classes.medium]: size === "medium"},
                {[classes.large]: size === "large"},
                {[classes.white]: white},
                {[classes.whiteText]: color === "whiteText"},
                {[classes.negative]: color === "negative"},
                {[classes.blackText]: color === "black"},
                {[classes.danger]: color === "danger"},
                {[classes.disabled]: disabled},
                {[classes.textDisabled]: disabled && variant === "text"}
            )}
            color={color !== "black" && color !== "negative" && color !== "danger" && color !== "whiteText" ? color : "default"}
            disabled={disabled}
            disableElevation={disableElevation}
            disableFocusRipple={disableFocusRipple}
            disableRipple={disableRipple}
            endIcon={!loading && endIcon}
            fullWidth={fullWidth}
            href={href}
            size={size}
            startIcon={!loading && startIcon}
            variant={variant}
            onClick={handleClick}
            onBlur={handleBlur}
            onFocus={handleFocus}
            ref={buttonRef}
        >
            {loading && (
                <CircularProgress
                    size={22}
                    thickness={4.5}
                    color={"inherit"}
                    style={{position: "absolute"}}
                />
            )}

            <div className={clsx({[classes.hidden]: loading})}>
                {children}
            </div>
        </Button>
    );
};

CustomButton.defaultProps = {
    color: "primary",
    disabled: false,
    size: "medium",
    variant: "text",
    white: false,
    loading: false,
};

CustomButton.propTypes = {
    children: PropTypes.any,
    color: PropTypes.string,
    disabled: PropTypes.bool,
    disableElevation: PropTypes.bool,
    disableFocusRipple: PropTypes.bool,
    disableRipple: PropTypes.bool,
    endIcon: PropTypes.element,
    fullWidth: PropTypes.bool,
    href: PropTypes.string,
    size: PropTypes.string,
    startIcon: PropTypes.element,
    variant: PropTypes.string,
    onClick: PropTypes.func,
    className: PropTypes.string,
    buttonRef: PropTypes.any,
    white: PropTypes.bool,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    loading: PropTypes.bool
};

export default CustomButton;
